/* Isolated copy of ayaq-size-guide.js with lazy data loading and editor lifecycle support. */
(function () {
  const sectionId = 'ayaq-size-guide-data';
  const metricMap = {
    height: 'height_cm',
    chest: 'chest_cm',
    waist: 'waist_cm',
    hips: 'hips_cm',
  };
  let guidesPromise;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else fn();
  }

  function initialGender(dialog) {
    return dialog.closest('.ayaq-pdp')?.dataset.productGender === 'femme'
      ? 'femme'
      : 'homme';
  }

  function fetchGuides() {
    if (guidesPromise) return guidesPromise;
    const url = new window.URL(window.location.href);
    url.searchParams.set('section_id', sectionId);
    guidesPromise = fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    })
      .then(function (response) {
        if (!response.ok) throw new Error('size guide unavailable');
        return response.text();
      })
      .then(function (html) {
        const doc = new window.DOMParser().parseFromString(html, 'text/html');
        const data = doc.querySelector('[data-ayaq-size-guides]');
        return data ? JSON.parse(data.textContent) : null;
      })
      .catch(function () {
        return null;
      });
    return guidesPromise;
  }

  function numberValue(dialog, key) {
    const input = dialog.querySelector('[data-measure="' + key + '"]');
    const value = parseFloat(
      String((input && input.value) || '').replace(',', '.'),
    );
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  function hasAnyValue(values) {
    return Object.keys(values).some(function (key) {
      return values[key] !== null;
    });
  }

  function recommend(guide, values) {
    const filled = Object.keys(values).filter(function (key) {
      return values[key] !== null;
    });
    if (!filled.length || !guide || !Array.isArray(guide.sizes_order)) {
      return null;
    }
    let best = null;
    guide.sizes_order.forEach(function (label) {
      const size =
        guide.sizes && guide.sizes['size_' + String(label).toLowerCase()];
      if (!size) return;
      let distance = 0;
      let matches = 0;
      filled.forEach(function (key) {
        const range = size[metricMap[key]];
        const value = values[key];
        if (
          !range ||
          typeof range.min !== 'number' ||
          typeof range.max !== 'number'
        ) {
          return;
        }
        if (value < range.min) distance += range.min - value;
        else if (value > range.max) distance += value - range.max;
        else matches += 1;
      });
      const score = distance * 100 - matches;
      if (!best || score < best.score) {
        best = {
          label: size.label,
          eu: size.eu,
          us: size.us,
          exact: distance === 0,
          score: score,
        };
      }
    });
    return best;
  }

  function closeDialog(dialog) {
    if (dialog.close) dialog.close();
    else {
      dialog.removeAttribute('open');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }
  }

  function setupDialog(dialog) {
    const sectionId = dialog.closest('.ayaq-pdp').dataset.section;
    const title = dialog.querySelector('.ayaq-size-guide-title');
    if (title) {
      title.id = `AyaqSizeGuideTitle-${sectionId}`;
      dialog.setAttribute('aria-labelledby', title.id);
    }
    dialog.addEventListener('close', function () {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    });

    dialog.addEventListener('change', function (event) {
      event.stopPropagation();
    });

    dialog.addEventListener('input', function (event) {
      event.stopPropagation();
    });

    // Tab buttons switching logic
    const tabButtons = dialog.querySelectorAll('.ayaq-size-guide-tab-button');
    const tabPanels = dialog.querySelectorAll('.ayaq-size-guide-tab-panel');
    tabButtons.forEach(function (button) {
      const panel = dialog.querySelector('#tab-panel-' + button.dataset.tab);
      if (panel) {
        button.id = `AyaqSizeGuideTab-${sectionId}-${button.dataset.tab}`;
        panel.id = `AyaqSizeGuidePanel-${sectionId}-${button.dataset.tab}`;
        panel.dataset.tab = button.dataset.tab;
        button.setAttribute('aria-controls', panel.id);
        panel.setAttribute('aria-labelledby', button.id);
      }
      button.addEventListener('click', function () {
        const targetTab = button.dataset.tab;
        tabButtons.forEach(function (btn) {
          btn.classList.remove('active');
          btn.setAttribute('aria-selected', 'false');
        });
        tabPanels.forEach(function (panel) {
          panel.classList.remove('active');
        });
        button.classList.add('active');
        button.setAttribute('aria-selected', 'true');
        const targetPanel = Array.from(tabPanels).find(
          (panel) => panel.dataset.tab === targetTab,
        );
        if (targetPanel) {
          targetPanel.classList.add('active');
        }
      });
    });

    const tabs = dialog.querySelector('.ayaq-size-guide-tabs');
    const selectTab = (button) => {
      tabButtons.forEach((tab) => {
        tab.tabIndex = tab === button ? 0 : -1;
      });
    };
    tabButtons.forEach((button) => {
      button.tabIndex = button.classList.contains('active') ? 0 : -1;
      button.addEventListener('click', () => selectTab(button));
    });
    tabs?.addEventListener('keydown', (event) => {
      const buttons = Array.from(tabButtons);
      const index = buttons.indexOf(document.activeElement);
      if (index < 0) return;
      let next = index;
      if (event.key === 'ArrowRight') next = (index + 1) % buttons.length;
      else if (event.key === 'ArrowLeft') {
        next = (index + buttons.length - 1) % buttons.length;
      } else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = buttons.length - 1;
      else return;
      event.preventDefault();
      buttons[next].click();
      buttons[next].focus();
    });

    const genderSelect = dialog.querySelector('[data-measure="gender"]');
    if (genderSelect) {
      genderSelect.value = initialGender(dialog);
    }

    const closeBtn = dialog.querySelector('.ayaq-size-guide-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        closeDialog(dialog);
      });
    }

    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) closeDialog(dialog);
    });

    const submitBtn = dialog.querySelector('.ayaq-size-guide-submit');
    if (submitBtn) {
      submitBtn.addEventListener('click', function () {
        const result = dialog.querySelector('.ayaq-size-guide-result');
        if (!result) return;

        const t = result.dataset;
        const gender = genderSelect
          ? genderSelect.value
          : initialGender(dialog);
        const values = {
          height: numberValue(dialog, 'height'),
          chest: numberValue(dialog, 'chest'),
          waist: numberValue(dialog, 'waist'),
          hips: numberValue(dialog, 'hips'),
        };

        if (!hasAnyValue(values)) {
          result.textContent =
            t.errorEmpty || 'Veuillez entrer au moins une mensuration.';
          return;
        }

        fetchGuides().then(function (guides) {
          const guide = guides && guides[gender];
          if (!guide) {
            result.textContent =
              t.errorUnavailable || 'Guide de tailles indisponible.';
            return;
          }
          const size = recommend(guide, values);
          if (!size) {
            result.textContent =
              t.errorNoMatch || 'Aucune taille correspondante.';
            return;
          }
          const details = [
            size.eu ? 'EU ' + size.eu : '',
            size.us ? 'US ' + size.us : '',
          ]
            .filter(Boolean)
            .join(' · ');
          const prefix = size.exact
            ? t.resultExact || 'Taille recommandée : '
            : t.resultApprox || 'Taille la plus proche : ';
          result.textContent =
            prefix + size.label + (details ? ' (' + details + ')' : '') + '.';
        });
      });
    }

    dialog.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' && event.target.matches('input')) {
        event.preventDefault();
        const submitBtn = dialog.querySelector('.ayaq-size-guide-submit');
        if (submitBtn) submitBtn.click();
      }
    });
  }

  function init(root = document) {
    root.querySelectorAll('.ayaq-pdp').forEach((section) => {
      const triggers = section.querySelectorAll(
        '[data-ayaq-size-guide-trigger]',
      );
      const dialog = section.querySelector('.ayaq-size-guide-dialog');
      if (!triggers.length || !dialog || dialog.dataset.pdpInitialized) return;
      dialog.dataset.pdpInitialized = 'true';
      setupDialog(dialog);
      triggers.forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          fetchGuides();
          dialog.showModal();
          document.documentElement.style.overflow = 'hidden';
          document.body.style.overflow = 'hidden';
        });
      });
    });
  }

  ready(() => init());
  document.addEventListener('shopify:section:load', (event) =>
    init(event.target),
  );
  document.addEventListener('shopify:section:unload', (event) => {
    const dialog = event.target.querySelector('.ayaq-size-guide-dialog[open]');
    if (dialog) closeDialog(dialog);
  });
})();
